import React from "react";
import { Link } from "react-router-dom";
import { PassportStamp } from "../passport/PassportStamp";
import { levelName, stampDate } from "../passport/stampStyle";
import type { Passport } from "../../services/api/travelHealth";
import { useI18nStore } from "../../stores/i18nStore";

interface Props {
  passport: Passport | null;
  loading: boolean;
  failed: boolean;
  /** The next trip that can still earn its stamp, if any. */
  nextTrip: { id: string; city: string } | null;
}

/**
 * Your passport (UX_CONCEPT.md §5.1 item 7, §7): the stamps earned so far,
 * the level, and the next stamp drawn dashed until its trip is ready.
 */
export const PassportSection: React.FC<Props> = ({
  passport,
  loading,
  failed,
  nextTrip,
}) => {
  const { t, language } = useI18nStore();

  if (loading) {
    return <div className="h-[170px] rounded-[22px] bg-white animate-pulse" />;
  }
  if (failed || !passport) {
    return (
      <section
        aria-label={t.passportTitle}
        className="rounded-[22px] bg-white p-5"
      >
        <h2 className="font-display text-[19px] font-bold text-ink">
          {t.passportTitle}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">{t.passportFailed}</p>
      </section>
    );
  }

  const count = passport.count;
  const level = levelName(passport.level, t);
  const waiting =
    nextTrip && !passport.stamps.some((s) => s.travelId === nextTrip.id)
      ? nextTrip
      : null;

  let caption: React.ReactNode;
  if (waiting) {
    const reachesNext = passport.next && count + 1 >= passport.next.at;
    caption = (
      <>
        {t.passportNextStamp.replace("{city}", waiting.city)}
        {reachesNext && passport.next && (
          <>
            {" "}
            {t.passportOneMore.split("{level}")[0]}
            <strong className="text-ink">
              {levelName(passport.next.level, t)}
            </strong>
            {t.passportOneMore.split("{level}")[1]}
          </>
        )}
      </>
    );
  } else if (count === 0) {
    caption = t.passportFirst;
  } else if (passport.next) {
    caption = t.passportToNext
      .replace("{count}", String(passport.next.at - count))
      .replace("{level}", levelName(passport.next.level, t));
  } else {
    caption = t.passportTop;
  }

  return (
    <section
      aria-label={t.passportTitle}
      className="rounded-[22px] bg-white p-5"
    >
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="font-display text-[19px] font-bold text-ink">
          {t.passportTitle}
        </h2>
        <span className="text-xs text-muted-foreground">
          {(count === 1 ? t.passportOneStamp : t.passportStamps).replace(
            "{count}",
            String(count),
          )}{" "}
          · {level}
        </span>
      </div>

      {(count > 0 || waiting) && (
        <ul className="-mx-1 mt-4 flex items-center gap-3 overflow-x-auto px-1 py-2">
          {passport.stamps.map((stamp, i) => (
            <li key={stamp.travelId} aria-label={stamp.city}>
              <PassportStamp
                city={stamp.city}
                ordinal={count - i}
                detail={stampDate(
                  stamp.departureDate ?? stamp.earnedAt,
                  language,
                )}
              />
            </li>
          ))}
          {waiting && (
            <li>
              <Link
                to={`/traveler/travel/${waiting.id}/readiness`}
                aria-label={t.passportOpenTrip.replace("{city}", waiting.city)}
                className="block rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-600"
              >
                <PassportStamp
                  city={waiting.city}
                  detail={t.passportAt100}
                  pending
                />
              </Link>
            </li>
          )}
        </ul>
      )}

      <p className="mt-3 text-[13px] leading-snug text-slate-600">{caption}</p>
    </section>
  );
};
